import { createFileRoute } from "@tanstack/react-router";
import { ServicePage } from "@/components/ServicePage";
import { RelatedServicesLinks } from "@/components/RelatedServicesLinks";
import {
  ShoppingCart,
  Search,
  Tag,
  BarChart3,
  Layers,
  Zap,
  Store,
  Package,
  Sparkles,
  Coffee,
  Shirt,
  Gift,
} from "lucide-react";

export const Route = createFileRoute("/ecommerce-seo-colorado")({
  head: () => ({
    meta: [
        { name: "description", content: "Colorado eCommerce SEO for Shopify and WooCommerce stores. Drive organic product traffic, fix technical issues, and grow revenue with a Colorado-based team." },
      { title: "eCommerce SEO Colorado | Shopify & WooCommerce" },
      {
        name: "description",
        content:
          "eCommerce SEO for Colorado Shopify, WooCommerce, and BigCommerce stores. Category, product, and technical SEO that grows organic revenue — month-to-month, transparent pricing.",
      },
      { property: "og:title", content: "eCommerce SEO Colorado | Shopify & WooCommerce" },
    ],
    links: [
      { rel: "canonical", href: "https://coloradowebimpressions.com/ecommerce-seo-colorado" },
    ],
  }),
  component: () => (
    <ServicePage
      slug="ecommerce-seo"
      serviceName="eCommerce SEO"
      pricingRoute="/pricing"
      eyebrow="Colorado eCommerce SEO"
      title="eCommerce SEO for Colorado Shopify & WooCommerce Stores"
      goldWord="Shopify & WooCommerce"
      description="Grow organic revenue from your Colorado online store. Category-page SEO, product schema, technical fixes, and content built around real buying intent — not enterprise-priced fluff."
      intro={{
        heading: "Most eCommerce SEO advice was written for Amazon, not your store.",
        body: [
          "Colorado-based Shopify, WooCommerce, and BigCommerce stores compete in a different game than the national enterprise eCommerce category most agencies sell into. You don't need a $15K/month enterprise contract — you need category pages that rank, product pages with proper schema, a fast technical foundation, and content that captures real buying-intent searches.",
          "We build eCommerce SEO that compounds: keyword research tied to your actual product catalog, category-page optimization, internal-link architecture that distributes authority, product schema for rich results, and content that earns links and ranks for the long-tail queries enterprise stores ignore.",
        ],
      }}
      benefits={[
        "Category & collection page SEO",
        "Product schema for rich results",
        "Technical SEO (speed, crawl, indexation)",
        "Buyer-intent content that ranks",
        "Internal-link architecture for authority flow",
        "Conversion-rate work tied to organic traffic",
      ]}
      features={[
        { icon: Layers, title: "Category SEO", desc: "Optimize the category & collection pages that drive 60-70% of organic eCommerce revenue." },
        { icon: Tag, title: "Product Schema", desc: "Product, Offer, AggregateRating, and FAQ schema for rich results in Google search." },
        { icon: Zap, title: "Technical SEO", desc: "Core Web Vitals, faceted-nav crawl management, canonical fixes, and indexation control for Shopify, Woo, and BigCommerce." },
        { icon: Search, title: "Keyword Research", desc: "Buying-intent keyword mapping tied to your real product catalog and margins." },
        { icon: BarChart3, title: "Revenue Reporting", desc: "Reports tied to organic revenue, AOV, and ROAS — not just sessions." },
        { icon: ShoppingCart, title: "CRO + SEO", desc: "On-page work that lifts conversion AND ranking together — not one at the expense of the other." },
      ]}
      frameworkHeading="The Colorado eCommerce SEO Method"
      frameworkIntro="A 6-step framework for growing organic revenue from your Colorado online store."
      framework={[
        { number: "01", title: "Catalog & Keyword Map", desc: "Map your real product catalog to buyer-intent keywords. Identify the categories and products with the most opportunity." },
        { number: "02", title: "Technical Foundation", desc: "Speed, schema, faceted-nav, canonicals, indexation. Fix the under-the-hood issues silently capping your rankings." },
        { number: "03", title: "Category-Page SEO", desc: "Rewrite category and collection pages with real content, internal links, and buyer-intent targeting." },
        { number: "04", title: "Product-Page Optimization", desc: "Unique product copy, proper schema, review integration, and internal links from supporting content." },
        { number: "05", title: "Content & Authority", desc: "Buying guides, comparison content, and helpful long-tail content that earns links and captures top-of-funnel demand." },
        { number: "06", title: "Track Revenue, Refine", desc: "Monthly reports tied to organic revenue and AOV. Quarterly strategy reviews to compound results." },
      ]}
      industries={[
        { icon: Shirt, name: "Apparel & Outdoor", desc: "Colorado outdoor brands — apparel, gear, accessories — that need category and collection rankings." },
        { icon: Coffee, name: "Food & Beverage", desc: "Coffee roasters, breweries, distilleries, specialty foods shipping nationally from Colorado." },
        { icon: Sparkles, name: "Beauty & Wellness", desc: "Cosmetics, supplements, and wellness brands with subscription and bundle revenue." },
        { icon: Package, name: "Home Goods", desc: "Home, decor, and lifestyle brands with rich category structures." },
        { icon: Gift, name: "Gift & Specialty", desc: "Holiday, gift, and specialty product stores with seasonal demand cycles." },
        { icon: Store, name: "Brick + Click", desc: "Colorado retailers running both physical and online — BOPIS, local inventory, hybrid SEO." },
      ]}
      faqs={[
        { q: "Do you work with Shopify, WooCommerce, and BigCommerce?", a: "Yes — Shopify is our most common platform, but we work in WooCommerce, BigCommerce, and Magento as well. Each platform has its own technical SEO quirks and we know them." },
        { q: "How long until eCommerce SEO produces revenue?", a: "Category-page wins typically show in 60–120 days. Product-page and long-tail content compound over 6–12 months. Most clients see meaningful organic revenue lift by month 4–6." },
        { q: "What's the difference between eCommerce SEO and regular SEO?", a: "eCommerce SEO leans heavily on category pages, faceted-nav management, product schema, and internal-link architecture. The keyword research is tied to actual products and margins, and reporting is revenue-based instead of traffic-based." },
        { q: "Can you handle international or multi-region eCommerce SEO?", a: "Yes — hreflang, currency, and regional store setups are standard for the Colorado outdoor and apparel brands we work with that ship internationally." },
        { q: "Do you only work with Colorado eCommerce stores?", a: "Our SEO services are Colorado-only. eCommerce stores headquartered in Colorado ship anywhere, but we focus the agency on Colorado-based businesses." },
      ]}
      sectionsAfterPricing={<RelatedServicesLinks current="seo-services" />}
    />
  ),
});
